"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import Hero from "@/components/tools/Hero";
import FAQ from "@/components/tools/FAQ";
import RelatedTopics from "@/components/tools/RelatedTopics";

const financingPartners = [
  {
    name: "CareCredit",
    logo: "/images/custom_modules_hydra_associations_images_cc.webp",
    url: "https://www.carecredit.com/",
    description: "Healthcare credit card for dental procedures",
    darkBg: false,
  },
  {
    name: "Cherry",
    logo: "/images/cherry-logo.svg",
    url: "https://withcherry.com/",
    description: "0% APR options, flexible payment plans",
    darkBg: false,
  },
  {
    name: "HFD",
    logo: "/images/hfd-logo.svg",
    url: "https://gohfd.com/",
    description: "99%+ approval rate, no credit impact",
    darkBg: false,
  },
  {
    name: "Proceed Finance",
    logo: "/images/proceed-logo.png",
    url: "https://www.proceedfinance.com/",
    description: "Low rates, long repayment terms",
    darkBg: true,
  },
];

const insuranceProviders = [
  {
    name: "Aetna",
    logo: "/images/custom_images_insurances_aetna.webp",
    url: "https://www.aetna.com/",
  },
  {
    name: "Blue Cross Blue Shield",
    logo: "/images/custom_images_insurances_bcbs.webp",
    url: "https://www.bcbs.com/",
  },
  {
    name: "Cigna",
    logo: "/images/custom_images_insurances_cigna.webp",
    url: "https://www.cigna.com/",
  },
  {
    name: "Delta Dental",
    logo: "/images/custom_images_insurances_delta.webp",
    url: "https://www.deltadental.com/",
  },
  {
    name: "Guardian",
    logo: "/images/custom_images_insurances_guardian.webp",
    url: "https://www.guardianlife.com/",
  },
  {
    name: "MetLife",
    logo: "/images/custom_images_insurances_metlife.webp",
    url: "https://www.metlife.com/",
  },
  {
    name: "United Health",
    logo: "/images/custom_images_insurances_uh.webp",
    url: "https://www.uhc.com/",
  },
];

const relatedTopics = [
  { label: "New Patients", url: "/new-patients" },
  { label: "Our Services", url: "/services" },
  { label: "Contact Us", url: "/contact" },
  { label: "Meet Your Dentist", url: "/meet-your-dentist" },
  { label: "Our Office", url: "/our-office" },
  { label: "Dental Cleanings", url: "/services/dental-cleanings" },
  { label: "Dental Implants", url: "/services/dental-implants" },
  { label: "All-on-4 Implants", url: "/services/all-on-4" },
];

const faqs = [
  {
    question: "What financing options do you offer?",
    answer:
      "We partner with CareCredit, Cherry, HFD, and Proceed to provide flexible payment plans. These options allow you to spread out the cost of your dental care over time, making treatments more affordable and accessible.",
  },
  {
    question: "What credit score do I need to qualify for financing?",
    answer:
      "Our financing partners work with a wide range of credit scores, typically from 500 to 850. Even if you have less-than-perfect credit, you may still qualify for a payment plan. We encourage you to apply and see what options are available to you.",
  },
  {
    question: "Do you offer interest-free financing?",
    answer:
      "Yes! We offer up to 2 years of interest-free financing for qualifying patients. This means you can pay for your dental treatment over time without any additional interest charges, making it easier to budget for your care.",
  },
  {
    question: "How long can I finance my dental treatment?",
    answer:
      "Our financing options range from 1 to 10 years, depending on the treatment cost and your preferences. Longer terms mean lower monthly payments, giving you the flexibility to choose what works best for your budget.",
  },
  {
    question: "What insurance plans do you accept?",
    answer:
      "We accept most major dental insurance plans including Aetna, Blue Cross Blue Shield, Cigna, Delta Dental, Guardian, MetLife, and United Health. Our team will help verify your benefits and maximize your coverage.",
  },
  {
    question: "Can I use insurance and financing together?",
    answer:
      "Absolutely! Many patients use their insurance to cover a portion of their treatment and then finance the remaining balance. This combination helps make even extensive dental work more manageable.",
  },
  {
    question: "How do I apply for financing?",
    answer:
      "Applying is quick and easy. You can apply online through any of our financing partners' websites, or our staff can help you apply in-office. Most applications receive an instant decision, so you'll know right away if you're approved.",
  },
  {
    question: "Will applying for financing affect my credit score?",
    answer:
      "Most of our financing partners offer a soft credit check for pre-qualification, which does not affect your credit score. A hard credit inquiry only occurs if you proceed with a full application after pre-approval.",
  },
];

const FinancingInsurance: React.FC = () => {
  return (
    <>
      <Hero
        imageSrc="/images/white-3d-printed-tooth-model-on-a-desk-with-a-blurred-background-of-a-woman-using-a-calculator-representing-dental-financing-and-treatment-costs.webp"
        imageAlt="Dental financing and treatment costs representation"
        title="Financing & Insurance"
        subtitle="Affordable dental care with flexible payment options and insurance support"
        ctaText="Book Your Consultation"
        ctaLink="https://app.nexhealth.com/appt/smile4me_dental_care?lid=77847&tracking_key=b2d71902-5ce0-43c1-bc95-24cdee27de9a"
      />

      <main className="flex-1 py-8">
        <div className="flex flex-col lg:flex-row mx-auto px-4 sm:px-8 md:px-12 max-w-7xl">
          <article className="flex-1 space-y-8 pr-0 lg:pr-8">
            {/* Introduction Section */}
            <section className="w-full py-6 sm:py-8">
              <div className="max-w-6xl mx-auto space-y-6">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center pb-4 after:content-[''] after:block after:w-[200px] after:h-[3px] after:mx-auto after:mt-4 after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]">
                  Making Dental Care Affordable
                </h2>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  At{" "}
                  <span className="font-semibold">Smile4Me Dental Care</span>, we
                  believe that everyone deserves access to quality dental care.
                  We understand that dental treatments can be a significant
                  investment, which is why we offer multiple financing options
                  and work with most major insurance providers to help make your
                  dental care as affordable as possible.
                </p>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  Whether you need a routine cleaning or a more extensive
                  procedure like dental implants, our team will work with you to
                  find a payment solution that fits your budget.
                </p>
              </div>
            </section>

            {/* Financing Options Section */}
            <section className="w-full py-6 sm:py-8 bg-gradient-to-b from-white to-gray-50 rounded-lg">
              <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-8">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center pb-4 after:content-[''] after:block after:w-[200px] after:h-[3px] after:mx-auto after:mt-4 after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]">
                  Flexible Financing Options
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {financingPartners.map((partner, idx) => (
                    <a
                      key={idx}
                      href={partner.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex flex-col items-center justify-center p-6 rounded-lg shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 min-h-[160px] border ${
                        partner.darkBg
                          ? "bg-gray-800 border-gray-700"
                          : "bg-white border-gray-100"
                      }`}
                      aria-label={`Learn more about ${partner.name} financing`}
                    >
                      {partner.logo ? (
                        <Image
                          src={partner.logo}
                          alt={partner.name}
                          width={150}
                          height={60}
                          className="object-contain mb-3"
                        />
                      ) : (
                        <span className="text-2xl font-bold text-gray-800 mb-3">
                          {partner.name}
                        </span>
                      )}
                      <p className={`text-sm text-center ${partner.darkBg ? "text-gray-300" : "text-gray-600"}`}>
                        {partner.description}
                      </p>
                    </a>
                  ))}
                </div>

                {/* Financing Highlights */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
                  <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-100">
                    <div className="text-4xl font-bold bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent mb-2">
                      500-850
                    </div>
                    <p className="text-gray-700 font-medium">
                      Credit Score Range
                    </p>
                    <p className="text-gray-500 text-sm mt-1">
                      Options for most credit profiles
                    </p>
                  </div>
                  <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-100">
                    <div className="text-4xl font-bold bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent mb-2">
                      2 Years
                    </div>
                    <p className="text-gray-700 font-medium">
                      Interest-Free Financing
                    </p>
                    <p className="text-gray-500 text-sm mt-1">
                      For qualifying patients
                    </p>
                  </div>
                  <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-100">
                    <div className="text-4xl font-bold bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent mb-2">
                      1-10 Years
                    </div>
                    <p className="text-gray-700 font-medium">
                      Flexible Terms
                    </p>
                    <p className="text-gray-500 text-sm mt-1">
                      Choose your payment timeline
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Insurance Section */}
            <section className="w-full py-6 sm:py-8">
              <div className="max-w-6xl mx-auto space-y-8">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center pb-4 after:content-[''] after:block after:w-[200px] after:h-[3px] after:mx-auto after:mt-4 after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]">
                  Insurance We Accept
                </h2>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed text-center max-w-3xl mx-auto">
                  We work with most major dental insurance providers to help
                  maximize your benefits. Our team will verify your coverage and
                  explain what your plan covers before treatment begins.
                </p>

                {/* Medicaid Notice */}
                <div className="bg-gray-100 border-l-4 border-gray-400 p-4 rounded-r-lg">
                  <p className="text-gray-700 font-medium">
                    Please Note: We are not a Medicaid provider.
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {insuranceProviders.map((provider, idx) => (
                    <a
                      key={idx}
                      href={provider.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300 min-h-[100px] border border-gray-100"
                      aria-label={`Visit ${provider.name} website`}
                    >
                      <Image
                        src={provider.logo}
                        alt={provider.name}
                        width={140}
                        height={60}
                        className="object-contain"
                        sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>

                <div className="text-center mt-6">
                  <p className="text-gray-600 text-sm">
                    Don&apos;t see your insurance listed?{" "}
                    <Link
                      href="/contact"
                      className="inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent hover:underline font-semibold"
                    >
                      Contact us
                    </Link>{" "}
                    to verify if we accept your plan.
                  </p>
                </div>
              </div>
            </section>

            {/* How It Works Section */}
            <section className="w-full py-6 sm:py-8 bg-gray-900 rounded-lg">
              <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-8">
                <h2 className="text-3xl sm:text-4xl font-bold text-white text-center pb-4 after:content-[''] after:block after:w-[200px] after:h-[3px] after:mx-auto after:mt-4 after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]">
                  How Financing Works
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">1</span>
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Apply Online
                    </h3>
                    <p className="text-gray-300 text-sm">
                      Quick application through our financing partners. Most
                      decisions are instant.
                    </p>
                  </div>
                  <div className="text-center p-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">2</span>
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Choose Your Plan
                    </h3>
                    <p className="text-gray-300 text-sm">
                      Select the payment term that fits your budget, from 1 to
                      10 years.
                    </p>
                  </div>
                  <div className="text-center p-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">3</span>
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Get Your Treatment
                    </h3>
                    <p className="text-gray-300 text-sm">
                      Start your dental care right away with manageable monthly
                      payments.
                    </p>
                  </div>
                </div>

                <div className="text-center mt-6">
                  <Link
                    href="/contact"
                    className="inline-block bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] hover:from-[#ECD05B] hover:to-[#AE7D2F] text-white font-semibold py-3 px-8 rounded-lg shadow-lg hover:shadow-inner [text-shadow:1px_1px_2px_rgba(0,0,0,0.5)] hover:scale-105 hover:-translate-y-1 transition-all duration-300 ease-in-out"
                    aria-label="Contact us about financing options"
                  >
                    Questions? Contact Us
                  </Link>
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <FAQ title="Financing & Insurance FAQs" faqs={faqs} />
          </article>

          <RelatedTopics topics={relatedTopics} />
        </div>
      </main>
    </>
  );
};

export default FinancingInsurance;
